/**
 * cmd-system.js
 * Minecraft-style command line with auto-suggestions and Tab completion.
 */

// dayCycle: 0 = 6am (morning), 0.25 = noon, 0.5 = 6pm (dusk), 0.75 = midnight
const TIME_PRESETS = {
  day:       0,
  sunrise:   0,
  noon:      0.25,
  sunset:    0.5,
  dusk:      0.5,
  night:     0.583,
  midnight:  0.75,
};

const TIME_PRESET_NAMES = Object.keys(TIME_PRESETS);

// ── Suggestion tree ────────────────────────────────────────────────────────
// Each node: { hint?, children?: string[] | () => string[] }
const CMD_TREE = {
  time:     { hint: '設定時間', sub: {
    set: { hint: '設定時間', children: () => TIME_PRESET_NAMES }
  }},
  gamemode: { hint: '切換模式', children: () => ['creative', 'survival', 'c', 's'] },
  gm:       { hint: '切換模式', children: () => ['creative', 'survival', 'c', 's'] },
  tp:       { hint: '傳送 /tp <x> <y> <z>' },
  give:     { hint: '給予物品', children: 'items' },
  clear:    { hint: '移除物品', children: 'items' },
  kill:     { hint: '自殺' },
  seed:     { hint: '顯示種子' },
  gamerule: { hint: '遊戲規則', sub: {
    keepinventory: { hint: 'true/false', children: () => ['true', 'false'] }
  }},
  help:     { hint: '列出指令' },
};
const ROOT_CMDS = Object.keys(CMD_TREE);

function buildItemLookup(ITEM_DEFS) {
  const byName = new Map();
  const byId   = new Map();
  const allNames = [];
  for (const [id, def] of Object.entries(ITEM_DEFS)) {
    byId.set(id, def);
    const lower = def.name.toLowerCase();
    byName.set(lower, id);
    allNames.push(lower);
    for (const word of lower.split(/\s+/)) {
      if (!byName.has(word)) byName.set(word, id);
    }
  }
  return { byName, byId, allNames };
}

export function createCmdSystem({
  controls,
  ui,
  dayNight,
  setCreativeModeRule,
  setKeepInventoryRule,
  showStatusToast,
  ITEM_DEFS,
  world,
}) {
  const { byName, byId, allNames } = buildItemLookup(ITEM_DEFS);

  let inputEl = null;
  let barEl   = null;
  let sugEl   = null;
  let open    = false;

  // suggestion state
  let suggestions = [];   // [{ text, hint }]
  let activeIdx   = -1;   // -1 = none selected

  // command history
  const history = [];
  const MAX_HISTORY = 50;
  let historyIdx = -1;    // -1 = not browsing history
  let savedInput = '';     // saves current input when entering history

  function mount(barElement, inputElement) {
    barEl   = barElement;
    inputEl = inputElement;
    sugEl   = barElement.querySelector('#cmd-suggestions');

    inputEl.addEventListener('keydown', onKeyDown);
    inputEl.addEventListener('keyup',   (e) => e.stopPropagation());
    inputEl.addEventListener('keypress',(e) => e.stopPropagation());
    inputEl.addEventListener('input', () => updateSuggestions());
  }

  function onKeyDown(e) {
    e.stopPropagation();

    if (e.code === 'Enter') {
      e.preventDefault();
      // if a suggestion is highlighted, apply it first
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        applySuggestion(suggestions[activeIdx].text);
        return;
      }
      const raw = inputEl.value.trim();
      if (raw !== '') {
        // save to history (avoid duplicating the last entry)
        if (history.length === 0 || history[history.length - 1] !== raw) {
          history.push(raw);
          if (history.length > MAX_HISTORY) history.shift();
        }
        runCommand(raw);
      }
      historyIdx = -1;
      savedInput = '';
      close();
      return;
    }

    if (e.code === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.code === 'Tab') {
      e.preventDefault();
      if (suggestions.length === 1) {
        applySuggestion(suggestions[0].text);
      } else if (activeIdx >= 0 && activeIdx < suggestions.length) {
        applySuggestion(suggestions[activeIdx].text);
      } else if (suggestions.length > 0) {
        // select first
        setActiveIdx(0);
      }
      return;
    }

    // Arrow up/down: navigate suggestions if present, otherwise browse history
    if (e.code === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setActiveIdx(activeIdx <= 0 ? suggestions.length - 1 : activeIdx - 1);
      } else if (history.length > 0) {
        if (historyIdx === -1) {
          savedInput = inputEl.value;
          historyIdx = history.length - 1;
        } else if (historyIdx > 0) {
          historyIdx--;
        }
        inputEl.value = history[historyIdx];
        inputEl.setSelectionRange(inputEl.value.length, inputEl.value.length);
      }
      return;
    }
    if (e.code === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setActiveIdx(activeIdx >= suggestions.length - 1 ? 0 : activeIdx + 1);
      } else if (historyIdx >= 0) {
        if (historyIdx < history.length - 1) {
          historyIdx++;
          inputEl.value = history[historyIdx];
        } else {
          historyIdx = -1;
          inputEl.value = savedInput;
        }
        inputEl.setSelectionRange(inputEl.value.length, inputEl.value.length);
      }
      return;
    }
  }

  // ── Suggestion engine ──────────────────────────────────────────────────
  function updateSuggestions() {
    const raw = inputEl.value;
    if (!raw.startsWith('/')) {
      clearSuggestions();
      return;
    }

    const text  = raw.slice(1); // without leading /
    const parts = text.split(/\s+/);
    const depth = text.endsWith(' ') ? parts.length : parts.length - 1;
    const partial = text.endsWith(' ') ? '' : (parts[parts.length - 1] || '').toLowerCase();

    let matches = [];

    if (depth === 0) {
      // suggesting root commands
      matches = ROOT_CMDS
        .filter(c => c.startsWith(partial))
        .map(c => ({ text: c, hint: CMD_TREE[c].hint || '' }));
    } else {
      const cmdName = parts[0].toLowerCase();
      const node = CMD_TREE[cmdName];
      if (node) {
        matches = getSubSuggestions(node, parts, depth, partial);
      }
    }

    // cap at 12
    suggestions = matches.slice(0, 12);
    activeIdx = -1;
    renderSuggestions();
  }

  function getSubSuggestions(node, parts, depth, partial) {
    // Walk down `sub` tree for intermediate args
    let current = node;
    for (let i = 1; i < depth; i++) {
      const arg = parts[i].toLowerCase();
      if (current.sub && current.sub[arg]) {
        current = current.sub[arg];
      } else {
        // no further sub-tree, try children at this level
        break;
      }
    }

    // Get candidate list
    let candidates;
    if (depth === 1 && current.sub) {
      // suggest sub-commands first
      candidates = Object.keys(current.sub).map(k => ({ text: k, hint: current.sub[k].hint || '' }));
      // also add direct children if any
      const extra = resolveChildren(current);
      if (extra) candidates = candidates.concat(extra.map(t => ({ text: t, hint: '' })));
    } else {
      const leaf = (depth >= 2 && current.sub) ? current.sub[parts[depth - 1]?.toLowerCase()] : current;
      const pool = leaf || current;
      const items = resolveChildren(pool);
      if (items) {
        candidates = items.map(t => ({ text: t, hint: '' }));
      } else {
        return [];
      }
    }

    if (!partial) return candidates;
    return candidates.filter(c => c.text.toLowerCase().startsWith(partial));
  }

  function resolveChildren(node) {
    if (!node || !node.children) return null;
    if (node.children === 'items') return allNames;
    if (typeof node.children === 'function') return node.children();
    return node.children;
  }

  function clearSuggestions() {
    suggestions = [];
    activeIdx = -1;
    if (sugEl) sugEl.innerHTML = '';
  }

  function renderSuggestions() {
    if (!sugEl) return;
    if (suggestions.length === 0) {
      sugEl.innerHTML = '';
      return;
    }

    sugEl.innerHTML = suggestions.map((s, i) => {
      const cls = i === activeIdx ? 'cmd-suggestion active' : 'cmd-suggestion';
      const hint = s.hint ? `<span class="cmd-hint">${esc(s.hint)}</span>` : '';
      return `<div class="${cls}" data-idx="${i}">${esc(s.text)}${hint}</div>`;
    }).join('');

    // click to select
    sugEl.querySelectorAll('.cmd-suggestion').forEach(el => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const idx = parseInt(el.dataset.idx, 10);
        if (idx >= 0 && idx < suggestions.length) {
          applySuggestion(suggestions[idx].text);
        }
      });
    });
  }

  function setActiveIdx(idx) {
    activeIdx = idx;
    renderSuggestions();
  }

  function applySuggestion(text) {
    const raw = inputEl.value;
    const prefix = raw.startsWith('/') ? '/' : '';
    const beforeSlash = raw.startsWith('/') ? raw.slice(1) : raw;
    const parts = beforeSlash.split(/\s+/);

    // replace the last partial token with the suggestion
    if (!beforeSlash.endsWith(' ') && parts.length > 0) {
      parts[parts.length - 1] = text;
    } else {
      parts.push(text);
    }

    inputEl.value = prefix + parts.join(' ') + ' ';
    clearSuggestions();
    inputEl.focus();
    // re-trigger suggestions for the next level
    updateSuggestions();
  }

  function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Open / close ───────────────────────────────────────────────────────
  function openCmd(mode = 'command') {
    if (!inputEl) return;
    open = true;
    barEl.classList.remove('hidden');
    const prefill = mode === 'command' ? '/' : '';
    inputEl.value = prefill;
    clearSuggestions();
    historyIdx = -1;
    savedInput = '';
    if (controls.isLocked) controls.unlock();
    requestAnimationFrame(() => {
      inputEl.focus();
      inputEl.setSelectionRange(prefill.length, prefill.length);
      if (mode === 'command') updateSuggestions();
    });
  }

  function close() {
    if (!inputEl) return;
    open = false;
    barEl.classList.add('hidden');
    inputEl.value = '';
    inputEl.blur();
    clearSuggestions();
  }

  function isOpen() { return open; }

  // ── Command dispatcher ─────────────────────────────────────────────────
  function runCommand(raw) {
    const text = raw.startsWith('/') ? raw.slice(1) : raw;
    const parts = text.trim().split(/\s+/);
    const cmd   = parts[0].toLowerCase();
    const args  = parts.slice(1);

    switch (cmd) {
      case 'time':      return cmdTime(args);
      case 'gamemode':
      case 'gm':        return cmdGamemode(args);
      case 'tp':        return cmdTp(args);
      case 'give':      return cmdGive(args);
      case 'kill':      return cmdKill();
      case 'seed':      return cmdSeed();
      case 'gamerule':  return cmdGamerule(args);
      case 'clear':     return cmdClear(args);
      case 'help':
      case '?':         return cmdHelp();
      default:
        toast(`未知指令：/${cmd}  輸入 /help 查看所有指令`, 'error');
    }
  }

  // ── /time set <preset|0-24000> ─────────────────────────────────────────
  function cmdTime(args) {
    if (args[0]?.toLowerCase() === 'set') args = args.slice(1);
    const raw = args[0]?.toLowerCase();
    if (!raw) return toast('/time set <day|noon|night|midnight|0-24000>', 'error');

    let dayCycle;
    if (raw in TIME_PRESETS) {
      dayCycle = TIME_PRESETS[raw];
    } else {
      const ticks = Number(raw);
      if (!Number.isFinite(ticks)) return toast(`無效時間：${raw}`, 'error');
      dayCycle = ((ticks % 24000) / 24000);
    }

    const dayCount = dayNight.getDayCount();
    dayNight.importState({ dayCycle, dayCount });
    toast(`時間已設定為 ${raw}`);
  }

  // ── /gamemode ──────────────────────────────────────────────────────────
  function cmdGamemode(args) {
    const val = args[0]?.toLowerCase();
    if (!val) return toast('/gamemode <c|creative|s|survival>', 'error');

    const creative = (val === 'c' || val === 'creative' || val === '1');
    const survival = (val === 's' || val === 'survival' || val === '0');

    if (!creative && !survival) return toast(`未知模式：${val}  (c=創造, s=生存)`, 'error');
    setCreativeModeRule(creative);
  }

  // ── /tp <x> <y> <z> ───────────────────────────────────────────────────
  function cmdTp(args) {
    if (args.length < 3) return toast('/tp <x> <y> <z>', 'error');
    const [rx, ry, rz] = args;
    const camPos = controls.getObject().position;

    const x = parseRelative(rx, camPos.x);
    const y = parseRelative(ry, camPos.y);
    const z = parseRelative(rz, camPos.z);

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      return toast('座標無效', 'error');
    }

    controls.getObject().position.set(x, y, z);
    toast(`已傳送至 ${fmt(x)} ${fmt(y)} ${fmt(z)}`);
  }

  // ── /give <item> [count] ──────────────────────────────────────────────
  function cmdGive(args) {
    if (!args[0]) return toast('/give <物品名稱或ID> [數量]', 'error');

    const countArg = args[args.length - 1];
    let count = 1;
    let nameParts = args;

    if (/^\d+$/.test(countArg) && args.length > 1) {
      count = Math.min(3456, Math.max(1, parseInt(countArg, 10)));
      nameParts = args.slice(0, -1);
    }

    const query = nameParts.join(' ').toLowerCase();
    const itemId = resolveItemId(query);

    if (!itemId) return toast(`找不到物品：${query}`, 'error');

    const def = byId.get(String(itemId));
    const added = ui.addItem(String(itemId), count);
    if (added) {
      toast(`已給予 ${def.name} ×${count}`);
    } else {
      toast('背包已滿，無法放入', 'error');
    }
  }

  // ── /kill ──────────────────────────────────────────────────────────────
  function cmdKill() {
    const camPos = controls.getObject().position;
    camPos.y = -9999;
    toast('你死了');
  }

  // ── /seed ──────────────────────────────────────────────────────────────
  function cmdSeed() {
    const seedEl = document.getElementById('world-seed-input');
    const seed = seedEl?.value ?? '(未知)';
    toast(`世界種子：${seed}`, '', 3.5);
  }

  // ── /gamerule ──────────────────────────────────────────────────────────
  function cmdGamerule(args) {
    const rule = args[0]?.toLowerCase();
    const val  = args[1]?.toLowerCase();

    if (!rule) {
      return toast('/gamerule <keepInventory> <true|false>', 'error');
    }

    const boolVal = (val === 'true' || val === '1') ? true
                  : (val === 'false' || val === '0') ? false
                  : null;

    if (rule === 'keepinventory') {
      if (boolVal === null) return toast('/gamerule keepInventory <true|false>', 'error');
      setKeepInventoryRule(boolVal);
      return;
    }

    toast(`未知規則：${rule}  (支援：keepInventory)`, 'error');
  }

  // ── /clear ─────────────────────────────────────────────────────────────
  function cmdClear(args) {
    if (args.length === 0) {
      toast('/clear 需指定物品名稱，如：/clear diamond 64', 'error');
      return;
    }

    const countArg = args[args.length - 1];
    let count = 64;
    let nameParts = args;
    if (/^\d+$/.test(countArg) && args.length > 1) {
      count = Math.min(3456, Math.max(1, parseInt(countArg, 10)));
      nameParts = args.slice(0, -1);
    }

    const query = nameParts.join(' ').toLowerCase();
    const itemId = resolveItemId(query);
    if (!itemId) return toast(`找不到物品：${query}`, 'error');

    const def = byId.get(String(itemId));
    const removed = ui.removeItem(String(itemId), count);
    if (removed) {
      toast(`已移除 ${def.name} ×${count}`);
    } else {
      toast(`背包中沒有 ${def.name}`, 'error');
    }
  }

  // ── /help ──────────────────────────────────────────────────────────────
  function cmdHelp() {
    const lines = [
      '/time set <day|noon|night|midnight|0-24000>',
      '/gamemode <c|s>  (c=創造 s=生存)',
      '/tp <x> <y> <z>  (支援 ~相對座標)',
      '/give <物品名稱或ID> [數量]',
      '/clear <物品名稱或ID> [數量]',
      '/gamerule keepInventory <true|false>',
      '/kill',
      '/seed',
    ].join('\n');
    toast(lines, '', 8);
  }

  // ── helpers ────────────────────────────────────────────────────────────
  function toast(text, type = '', duration = 2.5) {
    showStatusToast(text, type, duration);
  }

  function fmt(n) { return Math.round(n * 10) / 10; }

  function parseRelative(str, current) {
    if (typeof str !== 'string') return NaN;
    if (str.startsWith('~')) {
      const delta = str.length > 1 ? parseFloat(str.slice(1)) : 0;
      return current + (Number.isFinite(delta) ? delta : 0);
    }
    return parseFloat(str);
  }

  function resolveItemId(query) {
    if (/^\d+$/.test(query) && byId.has(query)) return query;
    if (byName.has(query)) return byName.get(query);
    for (const [id, def] of Object.entries(ITEM_DEFS)) {
      if (def.name.toLowerCase().includes(query)) return id;
    }
    return null;
  }

  return { mount, openCmd, close, isOpen, runCommand };
}
